import React, { useCallback, useMemo, useState } from 'react'
import PicklistField from 'core/components/validatedForm/DropdownField'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import useParams from 'core/hooks/useParams'
import useReactRouter from 'use-react-router'
import { routeManager } from 'core/utils/routes'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { createVolumeType, addVolumeTypeTenant } from './actions'
import ModalForm from 'core/elements/modal/ModalForm'
import { Route } from 'core/plugins/route'
import TextField from 'core/components/validatedForm/TextField'
import Text from 'core/elements/Text'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import { listTenants } from 'account/components/userManagement/tenants/new-actions'
import { tenantsSelector } from 'account/components/userManagement/tenants/selectors'
import VolumeBackendPicklist from './VolumeBackendPicklist'

const useStyles = makeStyles<Theme>((theme) => ({
  withUnits: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  unitsDropdown: {
    position: 'relative',
    bottom: 1,
  },
}))

const tenantColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'description',
    label: 'Description',
  },
]

interface Props {
  addRoute: Route
}

export default function CreateVolumeModal({ addRoute }: Props) {
  const { history } = useReactRouter()
  const classes = useStyles()
  const [errorMessage, setErrorMessage] = useState<{ title: string; message: string } | undefined>(
    undefined,
  )
  const defaultParams = {
    name: '',
    description: '',
    volumeBackend: '',
    metadata: [],
    tenants: [],
    public: true,
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { loading: loadingTenants } = useListAction(listTenants)
  const tenantsList = useSelector(tenantsSelector)

  const { update, updating, error, reset } = useUpdateAction(createVolumeType)
  const { update: addTenantFn, reset: resetAddTenant } = useUpdateAction(addVolumeTypeTenant)

  const submitForm = useCallback(async () => {
    if (params.metadata.some((data) => data.key === 'volume_backend_name')) {
      setErrorMessage({
        title: 'Invalid Key-Value Pair',
        message:
          'Cannot add volume_backend_name in Metadata Key-Value pair. Please select the Volume Backend from the dropdown.',
      })
      return
    }
    setErrorMessage(undefined)

    const inititalValue = !!params.volumeBackend
      ? { volume_backend_name: params.volumeBackend }
      : {}

    const metadata = params.metadata.reduce((accum, pair) => {
      return {
        ...accum,
        [pair.key]: pair.value,
      }
    }, inititalValue)

    const body = {
      volume_type: {
        name: params.name,
        description: params.description,
        extra_specs: metadata,
        'os-volume-type-access:is_public': params.public,
      },
    }

    const { success, response } = await update({ body })
    if (!success) {
      return
    }
    const volumeTypeId = response.id
    if (!params.public) {
      const promises = params.tenants.map((tenant) => {
        const tenantBody = {
          addProjectAccess: {
            project: tenant.id,
          },
        }
        return addTenantFn({ id: volumeTypeId, body: tenantBody })
      })
      await Promise.allSettled(promises)
    }
    if (success) handleClose()
  }, [params])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    resetAddTenant()
    history.push(routeManager.routes.openstack.volumeTypes.path())
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create New Volume Type`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating}
      error={error || errorMessage}
      submitTitle={`Create Volume Type`}
      maxWidth={1200}
    >
      <FormFieldSection title="Volume Type Configuration">
        <TextField
          id="name"
          label="Name"
          onChange={getParamsUpdater('name')}
          value={params.name}
          required
        />
        <TextField
          id="description"
          label="Description"
          onChange={getParamsUpdater('description')}
          value={params.description}
          required
        />
        <PicklistField
          DropdownComponent={VolumeBackendPicklist}
          id="volumeType"
          onChange={getParamsUpdater('volumeBackend')}
          value={params.volumeBackend}
          label="Volume Backend"
          includeNoneOption
        />
        <KeyValuesField
          id="metadata"
          label="Metadata"
          value={params.metadata}
          onChange={getParamsUpdater('metadata')}
          addLabel="Add Metadata"
          allowMultipleValues
        />
      </FormFieldSection>
      <FormFieldSection title="Tenant Access">
        <CheckboxField
          id="public"
          label="Make Public"
          value={params.public}
          onChange={getParamsUpdater('public')}
          info="If checked, volume type will be available for every tenant."
        />
        {!params.public && (
          <>
            <Text variant="body2">
              Select the tenants that should have access to this volume type.
            </Text>
            <ListTableField
              id="tenants"
              data={tenantsList}
              loading={loadingTenants}
              columns={tenantColumns}
              onChange={getParamsUpdater('tenants')}
              value={params.tenants}
              uniqueIdentifier="id"
              searchTargets={['name']}
              multiSelection
            />
          </>
        )}
      </FormFieldSection>
    </ModalForm>
  )
}
